import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { renderBlobBackground } from '../renderer/plasma';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import useCoreSession, { coreErrorMessage, formatMinor, moneyAmount } from '../../stores/coreSession';
import { solvencyLabel } from '../../utils/solvency';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';

const state = { runs: [], loaded: false, loading: false, error: null };

export const PURGE_JUDGMENTS = {
  THE_STREET_MULE: 'LEGACY MOVED. VINNIE PAID. ORBIS NOT TOUCHED.',
  THE_PARASITE_BROKER: 'EVERY ANOMALY SOLD. THE ARCHIVE FILED A CLEAN ABSENCE.',
  THE_SILENT_ARCHIVIST: 'THE CARRIER HELD. THE SIGNAL HELD. THE COAT PAID.',
  THE_SIGNAL_ARSONIST: 'THE FIRE WAS INDEXED. THE MARKET REMEMBERS.',
};

export function purgeJudgment(archetype) {
  return PURGE_JUDGMENTS[archetype] || 'THE ARCHIVE RECORDED AN UNCLASSIFIED RUN.';
}

function text(ctx, value, x, y, color = TERMINAL.TEXT) {
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, value, x, y, 1);
}

function clip(value, max = 24) {
  const textValue = String(value ?? '--');
  return textValue.length > max ? `${textValue.slice(0, max - 1)}…` : textValue;
}

function rule(ctx, x, y, w, color = TERMINAL.GREEN_DARK) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, 1);
}

function currentSnapshot() {
  const session = useCoreSession.getState();
  const core = session.state || {};
  const inventory = Array.isArray(core.inventory) ? core.inventory : [];
  const slots = inventory.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  return {
    status: session.status,
    cash: moneyAmount(core.cash),
    debt: moneyAmount(core.debt),
    heat: Number(core.heat ?? 0),
    slots,
    capacity: Number(core.inventoryCapacity || 0),
    day: session.run?.day ?? core.day ?? null,
    district: core.districtId || '--',
  };
}

async function loadRuns() {
  if (state.loading) return;
  state.loading = true;
  state.error = null;
  try {
    state.runs = await useCoreSession.getState().loadRuns(20);
    state.loaded = true;
  } catch (error) {
    state.error = coreErrorMessage(error);
  } finally {
    state.loading = false;
  }
}

function latestRun() {
  return state.runs.find((run) => run.result) || state.runs[0] || null;
}

function drawTop(ctx, width) {
  const run = latestRun();
  const result = run?.result;
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, width, 26);
  rule(ctx, 0, 25, width);
  text(ctx, 'DOPEOS // PURGE', 8, 5, TERMINAL.TEXT);
  if (width >= 500) text(ctx, result ? 'DAY 31 // RUN RESULT' : 'NO ACTIVE RUN', 232, 5, TERMINAL.TEXT_DIM);
  text(ctx, result ? 'SEALED' : 'UNSEALED', width - 72, 5, result ? TERMINAL.GREEN : TERMINAL.TEXT_DIM);
}

function drawResult(ctx, x, y, w, h) {
  const run = latestRun();
  const result = run?.result;
  DopeUI.Panel(ctx, x, y, w, h, result ? 'PURGE // SEALED RESULT' : 'PURGE // NO RESULT');
  if (!result) {
    const core = currentSnapshot();
    const rows = [
      ['RUN', run?.sessionId || '--'],
      ['STATUS', run ? String(run.status || '--').toUpperCase() : 'NO RUN'],
      ['DAY', core.day == null ? '--' : `${core.day}/30`],
      ['CASH', core.status === 'online' ? `$${core.cash.toFixed(2)}` : '--'],
      ['DEBT', core.status === 'online' ? `$${core.debt.toFixed(2)}` : '--'],
      ['COAT', `${core.slots}/${core.capacity || '--'}`],
      ['HEAT', `${core.heat}/1000`],
    ];
    let cursor = y + 28;
    rows.forEach(([label, value]) => {
      text(ctx, label, x + 12, cursor, TERMINAL.TEXT_DIM);
      text(ctx, String(value), x + 132, cursor, TERMINAL.TEXT);
      cursor += 18;
    });
    rule(ctx, x + 12, cursor + 2, w - 24);
    text(ctx, 'RUN NOT SEALED // COMPLETE 30 TURNS.', x + 12, cursor + 18, TERMINAL.TEXT_DIM);
    text(ctx, state.error || 'PURGE PREVIEW.', x + 12, cursor + 36, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.GREEN);
    return;
  }

  const rows = [
    ['RUN', clip(result.runId)],
    ['DAY', result.day],
    ['SEALED', clip(result.sealedAt)],
    ['CASH', formatMinor(result.cash)],
    ['DEBT', formatMinor(result.debt)],
    ['INVENTORY', formatMinor(result.inventoryValue)],
    ['NET WORTH', formatMinor(result.netWorth)],
    ['HRONIR', clip(`${result.hronirStatus} / ${result.hronirOutcome}`, 24)],
    ['ARCHIVE Δ', String(result.archivePointsDelta)],
    ['ARCHIVE TOTAL', String(result.archivePointsLifetime)],
    ['ARCHETYPE', clip(result.purgedArchetype || 'UNCLASSIFIED', 24)],
    ['VERDICT', solvencyLabel(result)],
  ];
  let cursor = y + 26;
  rows.forEach(([label, value]) => {
    text(ctx, label, x + 12, cursor, TERMINAL.TEXT_DIM);
    text(ctx, String(value), x + 140, cursor, label === 'NET WORTH' ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT);
    cursor += 16;
  });
  rule(ctx, x + 12, cursor + 2, w - 24);
  text(ctx, 'HMAC-SHA256', x + 12, cursor + 16, TERMINAL.GREEN);
  text(ctx, String(result.signature).slice(0, 32), x + 12, cursor + 32, TERMINAL.TEXT_DIM);
  text(ctx, String(result.signature).slice(32), x + 12, cursor + 46, TERMINAL.TEXT_DIM);
}

function drawLedger(ctx, x, y, w, h) {
  DopeUI.Panel(ctx, x, y, w, h, 'RUN LEDGER');
  if (!state.runs.length) {
    text(ctx, state.error || (state.loading ? 'SYNCING...' : 'NO RUNS RECORDED.'), x + 12, y + 30, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
    return;
  }
  state.runs.slice(0, 8).forEach((run, index) => {
    const rowY = y + 30 + index * 24;
    text(ctx, `${run.sessionId?.slice(-8) || '--'}`, x + 12, rowY, TERMINAL.TEXT_DIM);
    const label = run.result ? 'SEALED' : `${String(run.status || '--').toUpperCase()}`;
    text(ctx, `D${run.day ?? '--'} ${label}`, x + 104, rowY, run.result ? TERMINAL.GREEN : TERMINAL.TEXT);
  });
  text(ctx, `READ-ONLY // ${solvencyLabel(latestRun()?.result)}. NO CELEBRATION.`, x + 12, y + h - 18, TERMINAL.TEXT_DIM);
}

registerView('purge', function renderPurge(ctx, time, width, height) {
  interactiveRegistry.clear();
  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);
  ctx.save();
  ctx.globalAlpha = 0.12;
  renderBlobBackground(ctx, time, safe.width, safe.height);
  ctx.restore();

  if (!state.loaded && !state.loading && useCoreSession.getState().status === 'online') loadRuns();

  drawTop(ctx, safe.width);
  const mobile = safe.width < 500;
  if (mobile) {
    drawResult(ctx, 8, 32, safe.width - 16, 300);
    drawLedger(ctx, 8, 340, safe.width - 16, 190);
  } else {
    drawResult(ctx, 8, 36, 376, 280);
    drawLedger(ctx, 400, 36, 232, 280);
  }

  const footerY = mobile ? 538 : 328;
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, footerY, safe.width, safe.height - footerY);
  rule(ctx, 0, footerY, safe.width);
  text(ctx, 'TAB FOCUS   ENTER   ESC DESKTOP', 8, mobile ? footerY + 8 : 360, TERMINAL.TEXT_DIM);
  const backX = mobile ? 8 : safe.width - 200;
  const backY = mobile ? 584 : 336;
  const backW = mobile ? safe.width - 16 : 192;
  const backH = mobile ? 44 : 24;
  DopeUI.Button(ctx, backX, backY, backW, backH, 'BACK TO DESKTOP', 'normal');
  interactiveRegistry.register({
    id: 'purge-back', x: backX, y: backY, w: backW, h: backH,
    onClick: () => navigateTo('dopeos_home'),
  });
  if (!mobile) {
    DopeUI.Button(ctx, 8, 336, 100, 24, state.loading ? 'SYNC' : 'REFRESH', state.loading ? 'disabled' : 'normal');
    interactiveRegistry.register({
      id: 'purge-refresh', x: 8, y: 336, w: 100, h: 24,
      onClick: () => { state.loaded = false; loadRuns(); },
    });
    text(ctx, purgeJudgment(latestRun()?.result?.purgedArchetype).slice(0, 44), 120, 384, TERMINAL.GREEN);
  }
});
